import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BackButton from '../../components/common/BackButton';

const ManagePackages = () => {
    const { t, i18n } = useTranslation();
    const [packages, setPackages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const [formData, setFormData] = useState({
        nameAr: '',
        nameEn: '',
        descriptionAr: '',
        descriptionEn: '',
        price: '',
        features: []
    });
    const [newFeature, setNewFeature] = useState({ nameAr: '', nameEn: '', count: '' });

    useEffect(() => {
        fetchPackages();
    }, []);

    const fetchPackages = async () => {
        try {
            const response = await axios.get('https://hasanhaji.com/api/packages');
            setPackages(response.data);
            setLoading(false);
        } catch (error) {
            console.error('Error fetching packages:', error);
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleFeatureChange = (e) => {
        setNewFeature({ ...newFeature, [e.target.name]: e.target.value });
    };

    const addFeature = () => {
        if (newFeature.nameAr && newFeature.nameEn) {
            setFormData({
                ...formData,
                features: [...formData.features, { ...newFeature }]
            });
            setNewFeature({ nameAr: '', nameEn: '', count: '' });
        }
    };

    const removeFeature = (index) => {
        const updatedFeatures = formData.features.filter((_, i) => i !== index);
        setFormData({ ...formData, features: updatedFeatures });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingPackage) {
                await axios.put(`https://hasanhaji.com/api/packages/${editingPackage.id}`, formData);
            } else {
                await axios.post('https://hasanhaji.com/api/packages', formData);
            }
            fetchPackages();
            resetForm();
        } catch (error) {
            console.error('Error saving package:', error);
            alert(t('admin.packageSaveError'));
        }
    };

    const handleEdit = (pkg) => {
        setEditingPackage(pkg);
        setFormData({
            nameAr: pkg.nameAr,
            nameEn: pkg.nameEn,
            descriptionAr: pkg.descriptionAr,
            descriptionEn: pkg.descriptionEn,
            price: pkg.price,
            features: pkg.features || []
        });
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm(t('admin.deletePackageConfirm'))) {
            try {
                await axios.delete(`https://hasanhaji.com/api/packages/${id}`);
                fetchPackages();
            } catch (error) {
                console.error('Error deleting package:', error);
                alert(t('admin.packageDeleteError'));
            }
        }
    };

    const resetForm = () => {
        setFormData({
            nameAr: '',
            nameEn: '',
            descriptionAr: '',
            descriptionEn: '',
            price: '',
            features: []
        });
        setNewFeature({ nameAr: '', nameEn: '', count: '' });
        setEditingPackage(null);
        setShowForm(false);
    };

    if (loading) {
        return (
            <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-5">
            <div className="d-flex  align-items-center mb-4">
                <BackButton to="/admin" />

                <h2>{t('admin.managePackagesTitle')}</h2>

                <button
                    className="btn btn-primary  ms-auto"
                    onClick={() => setShowForm(!showForm)}
                >
                    <i className={`fas ${showForm ? 'fa-minus' : 'fa-plus'} me-2`}></i>
                    {showForm ? t('admin.cancel') : t('admin.addNewPackage')}
                </button>
            </div>

            {showForm && (
                <div className="card mb-4 shadow-sm border-0">
                    <div className="card-body p-4">
                        <h4 className="card-title mb-4 text-primary">
                            {editingPackage ? t('admin.editPackage') : t('admin.addNewPackage')}
                        </h4>
                        <form onSubmit={handleSubmit}>
                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('admin.packageNameAr')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="nameAr"
                                        value={formData.nameAr}
                                        onChange={handleInputChange}
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('admin.packageNameEn')}</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        name="nameEn"
                                        value={formData.nameEn}
                                        onChange={handleInputChange}
                                        required
                                        dir="ltr"
                                    />
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('admin.descriptionAr')}</label>
                                    <textarea
                                        className="form-control"
                                        name="descriptionAr"
                                        value={formData.descriptionAr}
                                        onChange={handleInputChange}
                                        rows="3"
                                        required
                                    />
                                </div>
                                <div className="col-md-6 mb-3">
                                    <label className="form-label">{t('admin.descriptionEn')}</label>
                                    <textarea
                                        className="form-control"
                                        name="descriptionEn"
                                        value={formData.descriptionEn}
                                        onChange={handleInputChange}
                                        rows="3"
                                        required
                                        dir="ltr"
                                    />
                                </div>
                            </div>

                            <div className="mb-3">
                                <label className="form-label">{t('admin.priceBHD')}</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    className="form-control"
                                    name="price"
                                    value={formData.price}
                                    onChange={handleInputChange}
                                    required
                                />
                            </div>

                            <div className="mb-3 bg-light p-3 rounded">
                                <h5 className="mb-3 text-secondary">{t('admin.packageFeatures')}</h5>
                                <div className="row g-2 mb-3">
                                    <div className="col-md-4">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="nameAr"
                                            placeholder={t('admin.featureNameAr')}
                                            value={newFeature.nameAr}
                                            onChange={handleFeatureChange}
                                        />
                                    </div>
                                    <div className="col-md-4">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="nameEn"
                                            placeholder={t('admin.featureNameEn')}
                                            value={newFeature.nameEn}
                                            onChange={handleFeatureChange}
                                            dir="ltr"
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <input
                                            type="text"
                                            className="form-control"
                                            name="count"
                                            placeholder={t('admin.featureCount')}
                                            value={newFeature.count || 0}
                                            onChange={handleFeatureChange}
                                        />
                                    </div>
                                    <div className="col-md-2">
                                        <button
                                            type="button"
                                            className="btn btn-success w-100"
                                            onClick={addFeature}
                                        >
                                            <i className="fas fa-plus"></i> {t('admin.addFeature')}
                                        </button>
                                    </div>
                                </div>

                                <ul className="list-group">
                                    {formData.features.map((feature, index) => (
                                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center">
                                            <span>
                                                <strong>{i18n.language === 'ar' ? feature.nameAr : feature.nameEn}</strong>
                                                <span className="text-muted ms-2">
                                                    / {i18n.language === 'ar' ? feature.nameEn : feature.nameAr}
                                                </span>
                                                {feature.count && <span className="badge bg-secondary ms-2">{feature.count}</span>}
                                            </span>
                                            <button
                                                type="button"
                                                className="btn btn-danger btn-sm"
                                                onClick={() => removeFeature(index)}
                                            >
                                                <i className="fas fa-times"></i>
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            <div className="d-flex gap-2 justify-content-end">
                                <button type="button" className="btn btn-secondary" onClick={resetForm}>
                                    {t('admin.cancel')}
                                </button>
                                <button type="submit" className="btn btn-primary px-4">
                                    {editingPackage ? t('admin.updatePackage') : t('admin.savePackage')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="row">
                {packages.map((pkg) => (
                    <div key={pkg.id} className="col-md-6 col-lg-4 mb-4">
                        <div className="card h-100 border-0 shadow-sm">
                            <div className="card-body">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                    <h5 className="card-title fw-bold text-primary mb-0">
                                        {i18n.language === 'ar' ? pkg.nameAr : pkg.nameEn}
                                    </h5>
                                    <h4 className="text-dark mb-0">{pkg.price} <small className="fs-6 text-muted">BHD</small></h4>
                                </div>

                                <p className="card-text text-muted small mb-3 border-bottom pb-2">
                                    {i18n.language === 'ar' ? pkg.descriptionAr : pkg.descriptionEn}
                                </p>

                                <div className="mt-3">
                                    <h6 className="small fw-bold text-uppercase text-secondary mb-2">{t('admin.packageFeatures')}:</h6>
                                    <ul className="list-unstyled small text-muted mb-0">
                                        {pkg.features && pkg.features.slice(0, 3).map((feature, index) => (
                                            <li key={index} className="mb-1">
                                                <i className="fas fa-check-circle text-success me-2"></i>
                                                {i18n.language === 'ar' ? feature.nameAr : feature.nameEn}
                                                {feature.count && ` (${feature.count})`}
                                            </li>
                                        ))}
                                        {pkg.features && pkg.features.length > 3 && (
                                            <li className="text-primary mt-1 fst-italic">
                                                {t('admin.andMoreFeatures', { count: pkg.features.length - 3 })}
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            </div>
                            <div className="card-footer bg-white border-0 pt-0 pb-3 d-flex justify-content-end gap-2">
                                <button
                                    className="btn btn-sm btn-outline-primary"
                                    onClick={() => handleEdit(pkg)}
                                >
                                    <i className="fas fa-edit me-1"></i> {t('admin.edit')}
                                </button>
                                <button
                                    className="btn btn-sm btn-outline-danger"
                                    onClick={() => handleDelete(pkg.id)}
                                >
                                    <i className="fas fa-trash-alt me-1"></i> {t('admin.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            {packages.length === 0 && !showForm && (
                <div className="text-center py-5">
                    <i className="fas fa-box-open fa-3x text-muted mb-3 opacity-50"></i>
                    <p className="text-muted">{t('admin.noPackagesFound')}</p>
                </div>
            )}
        </div>
    );
};

export default ManagePackages;
